/**
 * Start insight rules: compare home boat vs leaders across prestart views.
 */

import {
  findHomeRow,
  fleetMedianForColumn,
  formatNumber,
  getRowValue,
  getTeamName,
  leaders,
  leadersMean,
  meaningfulGap,
  rankByColumn,
  teamListLabel,
  toFiniteNumber,
} from '../fleetStats';
import { buildGapDetail, buildRecommendation, sortFindings } from '../templates';
import type { Evidence, Finding, FindingPoint, InsightRow, ShowMeAction, StartViewsBundle } from '../types';
import {
  START_DECOMPOSITION_RULES,
  START_OUTCOME_RULES,
  START_SETTING_RULES,
  type StartRuleDef,
  type StartSettingRuleDef,
} from './startRulebook';

function viewRows(bundle: StartViewsBundle, view: StartRuleDef['view'] | StartSettingRuleDef['view']): InsightRow[] {
  switch (view) {
    case 'prestart':
      return bundle.prestart;
    case 'acceleration':
      return bundle.acceleration;
    case 'maxbsp':
      return bundle.maxbsp;
    case 'reach':
      return bundle.reach;
    case 'leg1':
      return bundle.leg1;
    default:
      return [];
  }
}

function showMeFor(
  rule: StartRuleDef | StartSettingRuleDef,
  leaderTeams: string[],
  homeBoat: string
): ShowMeAction {
  const viewMap: Record<string, string> = {
    prestart: 'PRESTART',
    acceleration: 'ACCELERATION',
    maxbsp: 'MAX BSP',
    reach: 'REACH',
    leg1: 'LEG 1',
  };
  const ascending =
    'ascending' in rule && typeof rule.ascending === 'boolean'
      ? rule.ascending
      : 'better' in rule && rule.better === 'lower';
  const rankKey =
    'rankKey' in rule && rule.rankKey
      ? rule.rankKey
      : rule.metric;
  return {
    view: viewMap[rule.view] ?? 'PRESTART',
    sortKey: rankKey,
    sortDirection: ascending ? 'asc' : 'desc',
    highlightTeams: [homeBoat, ...leaderTeams],
  };
}

function evaluateMetricRule(
  rule: StartRuleDef,
  bundle: StartViewsBundle,
  homeBoat: string,
  raceSuffix: string
): Finding | null {
  const rows = viewRows(bundle, rule.view);
  if (rows.length < 3) {return null;}

  const homeRow = findHomeRow(rows, homeBoat);
  if (!homeRow) {return null;}

  const ascending = rule.ascending ?? rule.better === 'lower';
  const leaderRows = leaders(rows, rule.metric, homeBoat, ascending);
  if (leaderRows.length === 0) {return null;}

  const ours = toFiniteNumber(getRowValue(homeRow, rule.metric));
  const leadersAvg = leadersMean(leaderRows);
  const gapInfo = meaningfulGap(ours, leadersAvg, rule.threshold, rule.better);
  if (!gapInfo) {return null;}

  const leaderTeams = leaderRows.map((r) => r.team);
  const fleetMed = fleetMedianForColumn(rows, rule.metric);
  const evidence: Evidence[] = [
    {
      metric: rule.metric,
      label: rule.label,
      unit: rule.unit,
      ours,
      leaders: leadersAvg,
      fleetMedian: fleetMed,
      better: rule.better,
    },
  ];

  const ranked = rankByColumn(rows, rule.metric, ascending);
  const homeRank = ranked.find((r) => r.team.toLowerCase() === homeBoat.toLowerCase())?.rank;

  const severity = gapInfo.weAreWorse ? 'loss' : 'gain';
  const headline = gapInfo.weAreWorse ? rule.headlineLoss : rule.headlineGain;
  const detailParts = [
    buildGapDetail({
      metricLabel: rule.label,
      ours,
      leaders: leadersAvg,
      unit: rule.unit,
      leaderTeams,
      weAreWorse: gapInfo.weAreWorse,
      betterIsHigher: rule.better === 'higher',
    }),
  ];
  if (homeRank != null) {
    detailParts.push(`You ranked ${homeRank} of ${ranked.filter((r) => Number.isFinite(r.value)).length} on this metric.`);
  }

  return {
    id: `start-${rule.id}${raceSuffix}`,
    area: 'start',
    severity,
    headline: raceSuffix ? `${headline} (R${raceSuffix.replace(/^-r/, '')})` : headline,
    detail: detailParts.join(' '),
    evidence,
    recommendation: buildRecommendation({
      template: rule.recommendation,
      leaderTeams,
      metricLabel: rule.label,
      leadersValue: leadersAvg,
      unit: rule.unit,
    }),
    showMe: showMeFor(rule, leaderTeams, homeBoat),
  };
}

interface SettingHit {
  rule: StartSettingRuleDef;
  ours: number;
  leadersAvg: number;
  absGap: number;
  leaderTeams: string[];
  fleetMed: number | null;
}

function phaseHeading(view: StartSettingRuleDef['view']): string {
  return view === 'acceleration' ? 'At max acceleration' : 'At max boat speed';
}

/** Drop the phase suffix; the group heading already names it. */
function channelLabel(label: string): string {
  return label.replace(/ at max (accel|bsp)$/i, '');
}

function collectSettingHit(
  rule: StartSettingRuleDef,
  bundle: StartViewsBundle,
  homeBoat: string
): SettingHit | null {
  const rows = viewRows(bundle, rule.view);
  if (rows.length < 3) {return null;}

  const homeRow = findHomeRow(rows, homeBoat);
  if (!homeRow) {return null;}

  // Rank leaders by the primary performance metric of this view
  const primaryKey = rule.view === 'acceleration' ? 'Accel_max' : 'Bsp_max';
  const leaderRows = leaders(rows, primaryKey, homeBoat, false);
  if (leaderRows.length === 0) {return null;}

  const ours = toFiniteNumber(getRowValue(homeRow, rule.metric));
  const leaderSettingValues = leaderRows
    .map((lr) => toFiniteNumber(getRowValue(lr.row, rule.metric)))
    .filter((v): v is number => v != null);
  if (leaderSettingValues.length === 0 || ours == null) {return null;}

  const leadersAvg =
    leaderSettingValues.reduce((a, b) => a + b, 0) / leaderSettingValues.length;
  const absGap = Math.abs(ours - leadersAvg);
  if (absGap < rule.threshold) {return null;}

  return {
    rule,
    ours,
    leadersAvg,
    absGap,
    leaderTeams: leaderRows.map((r) => r.team),
    fleetMed: fleetMedianForColumn(rows, rule.metric),
  };
}

/** One card for every setup channel that differs from the leaders. */
function buildSettingsFinding(
  hits: SettingHit[],
  homeBoat: string,
  raceSuffix: string
): Finding | null {
  if (hits.length === 0) {return null;}

  const byView = new Map<StartSettingRuleDef['view'], SettingHit[]>();
  for (const hit of hits) {
    const bucket = byView.get(hit.rule.view) ?? [];
    bucket.push(hit);
    byView.set(hit.rule.view, bucket);
  }

  const points: FindingPoint[] = [];
  const evidence: Evidence[] = [];
  const viewOrder: StartSettingRuleDef['view'][] = ['acceleration', 'maxbsp'];

  for (const view of viewOrder) {
    const bucket = byView.get(view);
    if (!bucket || bucket.length === 0) {continue;}
    const ranked = [...bucket].sort((a, b) => b.absGap - a.absGap);
    const leaderTeams = ranked[0]?.leaderTeams ?? [];
    const group = `${phaseHeading(view)} · ${teamListLabel(leaderTeams)}`;

    for (const hit of ranked) {
      const unit = hit.rule.unit ? ` ${hit.rule.unit}` : '';
      points.push({
        id: `start-setting-${hit.rule.id}${raceSuffix}`,
        group,
        label: channelLabel(hit.rule.label),
        comparison: `${formatNumber(hit.ours)}${unit} vs leaders ${formatNumber(hit.leadersAvg)}${unit}`,
        delta: `Δ ${formatNumber(hit.absGap)}${unit}`,
        showMe: showMeFor(hit.rule, hit.leaderTeams, homeBoat),
      });
      evidence.push({
        metric: hit.rule.metric,
        label: hit.rule.label,
        unit: hit.rule.unit,
        ours: hit.ours,
        leaders: hit.leadersAvg,
        fleetMedian: hit.fleetMed,
        better: 'higher',
      });
    }
  }

  return {
    id: `start-settings${raceSuffix}`,
    area: 'start',
    severity: 'neutral',
    headline: 'Leaders used a different setup',
    detail: 'Channels where your boat differs from the leaders.',
    evidence,
    points,
  };
}

function leaderNames(finding: Finding, homeBoat: string): string[] {
  const home = homeBoat.trim().toLowerCase();
  return (finding.showMe?.highlightTeams ?? []).filter((team) => team.trim().toLowerCase() !== home);
}

/** One list row from a single-metric finding. Keeps that metric's Show me. */
function pointFromFinding(finding: Finding, homeBoat: string): FindingPoint | null {
  const ev = finding.evidence[0];
  if (!ev || ev.ours == null || ev.leaders == null) {return null;}
  const unit = ev.unit ? ` ${ev.unit}` : '';
  const leaders = leaderNames(finding, homeBoat);
  const leaderBit = leaders.length > 0 ? teamListLabel(leaders) : 'leaders';
  const label = finding.headline.replace(/\s*\(R[^)]+\)\s*$/, '');
  return {
    id: finding.id,
    label,
    comparison: `${formatNumber(ev.ours)}${unit} vs ${leaderBit} ${formatNumber(ev.leaders)}${unit}`,
    delta: `Δ ${formatNumber(Math.abs(ev.ours - ev.leaders))}${unit}`,
    showMe: finding.showMe,
  };
}

/** Outcome and decomposition metrics, still one finding per metric (for trends). */
function evaluateStartMetricFindings(
  bundle: StartViewsBundle,
  homeBoat: string,
  raceSuffix: string
): Finding[] {
  const findings: Finding[] = [];
  for (const rule of START_OUTCOME_RULES) {
    const finding = evaluateMetricRule(rule, bundle, homeBoat, raceSuffix);
    if (finding) {findings.push(finding);}
  }
  for (const rule of START_DECOMPOSITION_RULES) {
    const finding = evaluateMetricRule(rule, bundle, homeBoat, raceSuffix);
    if (finding) {findings.push(finding);}
  }
  return findings;
}

/** One card for every metric on the same side of the leaders. */
function buildSeverityFinding(
  items: Finding[],
  homeBoat: string,
  raceSuffix: string,
  severity: 'loss' | 'gain'
): Finding | null {
  const points = items
    .map((finding) => pointFromFinding(finding, homeBoat))
    .filter((point): point is FindingPoint => point != null);
  if (points.length === 0) {return null;}
  const raceLabel = raceSuffix ? ` (R${raceSuffix.replace(/^-r/, '')})` : '';
  const copy = severity === 'loss'
    ? {
        id: 'start-losses',
        headline: 'Behind the leaders',
        detail: 'Where your boat gave up ground.',
      }
    : {
        id: 'start-gains',
        headline: 'Ahead of the leaders',
        detail: 'Where your boat had the edge.',
      };
  return {
    id: `${copy.id}${raceSuffix}`,
    area: 'start',
    severity,
    headline: `${copy.headline}${raceLabel}`,
    detail: copy.detail,
    evidence: items.flatMap((finding) => finding.evidence),
    points,
  };
}

/** Build findings for a single race's start views. */
export function buildStartFindingsForRace(
  bundle: StartViewsBundle,
  homeBoat: string
): Finding[] {
  const raceSuffix = bundle.raceKey ? `-r${bundle.raceKey}` : '';
  const findings: Finding[] = [];
  const metrics = evaluateStartMetricFindings(bundle, homeBoat, raceSuffix);
  const lossCard = buildSeverityFinding(
    metrics.filter((finding) => finding.severity === 'loss'),
    homeBoat,
    raceSuffix,
    'loss'
  );
  if (lossCard) {findings.push(lossCard);}
  const gainCard = buildSeverityFinding(
    metrics.filter((finding) => finding.severity === 'gain'),
    homeBoat,
    raceSuffix,
    'gain'
  );
  if (gainCard) {findings.push(gainCard);}

  const settingHits: SettingHit[] = [];
  for (const rule of START_SETTING_RULES) {
    const hit = collectSettingHit(rule, bundle, homeBoat);
    if (hit) {settingHits.push(hit);}
  }
  const settingsFinding = buildSettingsFinding(settingHits, homeBoat, raceSuffix);
  if (settingsFinding) {findings.push(settingsFinding);}

  return sortFindings(findings);
}

/**
 * When race = All: run per race and surface recurring losses
 * (same metric worse than leaders in ≥ half of races, min 2).
 */
export function buildStartDayTrendFindings(
  perRaceBundles: StartViewsBundle[],
  homeBoat: string
): Finding[] {
  if (perRaceBundles.length < 2) {return [];}

  const lossCounts = new Map<string, { count: number; races: string[]; rule: StartRuleDef; sample: Finding }>();

  for (const bundle of perRaceBundles) {
    const raceSuffix = bundle.raceKey ? `-r${bundle.raceKey}` : '';
    const raceFindings = evaluateStartMetricFindings(bundle, homeBoat, raceSuffix);
    for (const f of raceFindings) {
      if (f.severity !== 'loss') {continue;}
      // Strip race suffix from id for aggregation: start-dtl_start-r1 → start-dtl_start
      const baseId = f.id.replace(/-r[\w.-]+$/, '');
      const rule =
        START_OUTCOME_RULES.find((r) => `start-${r.id}` === baseId) ??
        START_DECOMPOSITION_RULES.find((r) => `start-${r.id}` === baseId);
      if (!rule) {continue;}
      const entry = lossCounts.get(baseId) ?? {
        count: 0,
        races: [],
        rule,
        sample: f,
      };
      entry.count += 1;
      if (bundle.raceKey) {entry.races.push(bundle.raceKey);}
      entry.sample = f;
      lossCounts.set(baseId, entry);
    }
  }

  const minHits = Math.max(2, Math.ceil(perRaceBundles.length / 2));
  const ranked = [...lossCounts.entries()]
    .filter(([, entry]) => entry.count >= minHits)
    .sort((a, b) => b[1].count - a[1].count);
  if (ranked.length === 0) {return [];}

  const points: FindingPoint[] = [];
  const evidence: Evidence[] = [];
  for (const [baseId, entry] of ranked) {
    const point = pointFromFinding(entry.sample, homeBoat);
    if (!point) {continue;}
    const raceList = entry.races.length > 0 ? entry.races.join(', ') : `${entry.count} races`;
    points.push({
      ...point,
      id: `start-trend-${baseId}`,
      label: entry.rule.headlineLoss,
      comparison: `${entry.count} of ${perRaceBundles.length} races (${raceList}) · ${point.comparison}`,
    });
    evidence.push(...entry.sample.evidence);
  }
  if (points.length === 0) {return [];}

  return [{
    id: 'start-trends',
    area: 'start',
    severity: 'loss',
    headline: 'Recurring gaps vs the leaders',
    detail: `These showed up in at least half of ${perRaceBundles.length} races.`,
    evidence,
    points,
  }];
}

/** Flat entry: single race or day trend depending on input. */
export function buildStartFindings(
  input: StartViewsBundle | StartViewsBundle[],
  homeBoat: string
): Finding[] {
  if (Array.isArray(input)) {
    if (input.length === 0) {
      return [];
    }
    const first = input[0];
    if (input.length === 1 && first) {
      return buildStartFindingsForRace(first, homeBoat);
    }
    const perRace = input.flatMap((b) => buildStartFindingsForRace(b, homeBoat));
    const trends = buildStartDayTrendFindings(input, homeBoat);
    // Prefer trends + a short list of the worst single-race losses (cap total)
    const singleLosses = perRace.filter((f) => f.severity === 'loss').slice(0, 6);
    return sortFindings([...trends, ...singleLosses]);
  }
  return buildStartFindingsForRace(input, homeBoat);
}

/** Helper to read a team list for Show-me highlighting from any start rows. */
export function leadersTeamsForMetric(
  rows: InsightRow[],
  metric: string,
  homeBoat: string,
  ascending: boolean
): string[] {
  return leaders(rows, metric, homeBoat, ascending).map((r) => r.team);
}

export function homeTeamPresent(rows: InsightRow[], homeBoat: string): boolean {
  return findHomeRow(rows, homeBoat) != null || rows.some((r) => getTeamName(r).toLowerCase() === homeBoat.toLowerCase());
}

/** True when the home boat has a row in any start view for this race. */
export function homeInStartBundle(bundle: StartViewsBundle, homeBoat: string): boolean {
  if (!homeBoat.trim()) {return false;}
  return (
    homeTeamPresent(bundle.prestart, homeBoat) ||
    homeTeamPresent(bundle.acceleration, homeBoat) ||
    homeTeamPresent(bundle.maxbsp, homeBoat) ||
    homeTeamPresent(bundle.reach, homeBoat) ||
    homeTeamPresent(bundle.leg1, homeBoat)
  );
}
